import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogoMark } from './LogoMark';
import { InstallPrompt } from './InstallPrompt';

const nav = [
  { to: '/', label: 'Home', icon: '⌂' },
  { to: '/tournaments/new', label: 'Create', icon: '+' },
];

export function AppShell() {
  const { user, logout, branding } = useAuth();
  const location = useLocation();
  const items = user?.role === 'admin' ? [...nav, { to: '/admin', label: 'Admin', icon: '⚙' }] : nav;
  const pageName = location.pathname.startsWith('/tournaments/new') ? 'Create tournament' : location.pathname.startsWith('/tournaments/') ? 'Tournament console' : location.pathname.startsWith('/admin') ? 'Administration' : 'Tournament hub';
  const appName = branding?.appName || 'Pickle Social';

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside id="desktop-sidebar" className="brand-bg hidden min-h-screen flex-col p-6 text-white lg:flex">
        <LogoMark branding={branding} />
        <nav className="mt-12 space-y-2">
          {items.map((item) => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold transition ${isActive ? 'bg-white text-[var(--primary)] shadow-lg' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}><span className="grid h-8 w-8 place-items-center rounded-xl bg-current/10 text-xl">{item.icon}</span>{item.label}</NavLink>)}
        </nav>
        <div className="mt-auto rounded-3xl bg-white/10 p-4">
          <div className="mb-3 flex items-center gap-3">
            {user?.logoDataUrl ? <img src={user.logoDataUrl} alt="Account logo" className="h-10 w-10 rounded-xl bg-white object-contain p-1" /> : <div className="grid h-10 w-10 place-items-center rounded-xl accent-bg font-extrabold text-[var(--primary)]">{user?.username[0]?.toUpperCase()}</div>}
            <div className="min-w-0"><p className="truncate text-sm font-extrabold">{user?.username}</p><p className="text-xs text-white/60">{user?.role === 'admin' ? 'Administrator' : 'Organiser'}</p></div>
          </div>
          <button className="w-full rounded-xl border border-white/15 px-3 py-2 text-xs font-bold text-white/75 hover:bg-white/10 mt-2" onClick={() => void logout()}>Sign out</button>
        </div>
      </aside>
      <InstallPrompt />

      <main className="safe-bottom min-w-0">
        <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-[var(--secondary)]/90 px-4 py-3 backdrop-blur-xl sm:px-6 lg:px-10 lg:py-5">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <div className="flex items-center gap-3 lg:hidden">
              <LogoMark compact branding={branding} />
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-400">{appName}</p>
                <p className="text-sm font-extrabold brand-text">{pageName}</p>
              </div>
            </div>
            <div className="hidden lg:block">
              <p className="eyebrow">Control centre</p>
              <h1 className="text-xl font-extrabold brand-text">{pageName}</h1>
            </div>
            <div className="flex items-center gap-2">
              {user?.logoDataUrl && <img src={user.logoDataUrl} alt="" className="h-9 max-w-28 rounded-xl bg-white object-contain px-2 lg:hidden" />}
              {/* Mobile Logout button in top header */}
              <button
                type="button"
                onClick={() => void logout()}
                className="lg:hidden flex items-center gap-1 rounded-xl border border-slate-200 bg-white/80 px-2.5 py-1.5 text-xs font-extrabold text-slate-600 hover:text-red-600 hover:bg-white active:scale-95 transition shadow-sm"
                title="Sign out"
              >
                <span className="text-sm leading-none">⎋</span>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>
        <div className="mx-auto max-w-7xl p-4 pb-24 sm:p-6 sm:pb-24 lg:p-10">
          <Outlet />
          <footer className="mt-12 text-center text-xs text-slate-500">
            Designed by <a href="https://coetseedigital.com" target="_blank" rel="noopener noreferrer" className="font-bold hover:text-slate-800 transition">Coetsee Digital</a>
          </footer>
        </div>
      </main>

      {/* Mobile Bottom Navigation with dedicated Logout tab */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 grid border-t border-slate-200 bg-white/95 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur-xl lg:hidden"
        style={{ gridTemplateColumns: `repeat(${items.length + 1}, minmax(0, 1fr))` }}
      >
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `flex min-h-[64px] flex-col items-center justify-center gap-1 text-[11px] font-extrabold ${isActive ? 'brand-text' : 'text-slate-400'}`}
          >
            <span className="text-2xl leading-none">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => void logout()}
          className="flex min-h-[64px] flex-col items-center justify-center gap-1 text-[11px] font-extrabold text-slate-400 hover:text-red-600 active:text-red-600 transition"
        >
          <span className="text-2xl leading-none">⎋</span>
          Logout
        </button>
      </nav>
    </div>
  );
}
